import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { CategoryBasicInfoBlock } from './CategoryBasicInfoBlock';
import { CategoryHierarchyBlock } from './CategoryHierarchyBlock';
import { generateCategoryCode } from '../categoryCode';
import type { CategoryFormValues, CategoryLookups, SubCategoryFormValue } from '../types/categories.types';

interface CategoryFormProps {
  id: string;
  initialValues?: Partial<CategoryFormValues>;
  lookups?: CategoryLookups;
  onSubmit: (values: CategoryFormValues) => void;
  isLoading?: boolean;
}

const defaultForm: CategoryFormValues = {
  name: '',
  code: '',
  description: '',
  type: 'work',
  status: 'active',
  icon: 'ri-task-line',
  itemCount: 0,
  parentId: null,
  subCategories: [],
};

function createLocalId() {
  try {
    return crypto.randomUUID();
  } catch {
    return `local-${Math.random().toString(36).slice(2, 8)}`;
  }
}

export function CategoryForm({ id, initialValues, lookups, onSubmit, isLoading }: CategoryFormProps) {
  const [values, setValues] = useState<CategoryFormValues>(defaultForm);

  useEffect(() => {
    if (initialValues) {
      setValues((prev) => ({
        ...prev,
        ...initialValues,
      }));
    }
  }, [initialValues]);

  const handleChange = <K extends keyof CategoryFormValues>(key: K, value: CategoryFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  // Sub Category Handlers
  const addSubCategory = () => {
    setValues((prev) => ({
      ...prev,
      subCategories: [...prev.subCategories, { id: createLocalId(), name: '', code: '', description: '', subSubCategories: [] }],
    }));
  };

  const removeSubCategory = (id: string) => {
    setValues((prev) => ({
      ...prev,
      subCategories: prev.subCategories.filter((item) => item.id !== id),
    }));
  };

  const updateSubCategory = (id: string, patch: Partial<SubCategoryFormValue>) => {
    setValues((prev) => ({
      ...prev,
      subCategories: prev.subCategories.map((item) => {
        if (item.id !== id) return item;
        const next = { ...item, ...patch };
        if (patch.name !== undefined && !next.code.trim()) {
          next.code = generateCategoryCode(next.name);
        }
        return next;
      }),
    }));
  };

  // Sub-Sub Category Handlers
  const addSubSubCategory = (parentId: string) => {
    setValues((prev) => ({
      ...prev,
      subCategories: prev.subCategories.map((item) => {
        if (item.id !== parentId) return item;
        return {
          ...item,
          subSubCategories: [...(item.subSubCategories || []), { id: createLocalId(), name: '', code: '', description: '' }],
        };
      }),
    }));
  };

  const removeSubSubCategory = (parentId: string, id: string) => {
    setValues((prev) => ({
      ...prev,
      subCategories: prev.subCategories.map((item) => {
        if (item.id !== parentId) return item;
        return {
          ...item,
          subSubCategories: (item.subSubCategories || []).filter((child) => child.id !== id),
        };
      }),
    }));
  };

  const updateSubSubCategory = (parentId: string, id: string, patch: Partial<SubCategoryFormValue>) => {
    setValues((prev) => ({
      ...prev,
      subCategories: prev.subCategories.map((item) => {
        if (item.id !== parentId) return item;
        return {
          ...item,
          subSubCategories: (item.subSubCategories || []).map((child) => {
            if (child.id !== id) return child;
            const next = { ...child, ...patch };
            if (patch.name !== undefined && !next.code.trim()) {
              next.code = generateCategoryCode(next.name);
            }
            return next;
          }),
        };
      }),
    }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!values.name.trim()) return;
    const payload: CategoryFormValues = {
      ...values,
      code: values.code.trim() || generateCategoryCode(values.name),
    };
    onSubmit(payload);
  };

  return (
    <form 
      id={id} 
      onSubmit={handleSubmit} 
      className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:h-[calc(100vh-140px)] lg:min-h-[500px] lg:overflow-hidden overflow-y-auto custom-scrollbar"
    >
      <div className="lg:col-span-5 overflow-y-auto custom-scrollbar pr-1">
        <CategoryBasicInfoBlock
          values={values}
          parentOptions={lookups?.parentOptions || []}
          onChange={handleChange}
          isDisabled={isLoading}
        />
      </div>

      <div className="lg:col-span-7 flex flex-col min-h-0">
        <CategoryHierarchyBlock
          items={values.subCategories}
          onAddSub={addSubCategory}
          onRemoveSub={removeSubCategory}
          onUpdateSub={updateSubCategory}
          onAddChild={addSubSubCategory}
          onRemoveChild={removeSubSubCategory}
          onUpdateChild={updateSubSubCategory}
          isDisabled={isLoading}
        />
      </div>
    </form>
  );
}
